import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import "./game.css";
import game1DesktopBg from "../images/desktop/main.png";
import game1MobileBg from "../images/mob_bg/main_mob.png";
import timerIcon from "../images/timer.png";
import scoreIcon from "../images/score.png";
import { syncJoinLobbyFromUrlSearch } from "../../utils/joinLobbyFromUrl";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobbyName(locationState) {
  const fromState =
    typeof locationState?.lobbyName === "string"
      ? locationState.lobbyName.trim()
      : "";
  if (fromState) return fromState;
  try {
    const fromUrl = syncJoinLobbyFromUrlSearch(window.location.search);
    if (fromUrl) return fromUrl;
  } catch {
    // ignore
  }
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {
    // ignore storage failures
  }
  return "";
}

function decodeHtmlEntities(text) {
  if (text == null) return "";
  const ta = document.createElement("textarea");
  ta.innerHTML = String(text);
  return ta.value;
}

function rowPopupOne(row) {
  return String(
    row.option_one_popup ?? row.mythpopup ?? row.information ?? ""
  ).trim();
}

function rowPopupTwo(row) {
  return String(
    row.option_two_popup ?? row.factpopup ?? row.information ?? ""
  ).trim();
}

function explainHtmlForReveal(row, correctIdx0) {
  const information = String(row?.information ?? "").trim();
  const primary = correctIdx0 === 0 ? rowPopupOne(row) : rowPopupTwo(row);
  const fallback = correctIdx0 === 0 ? rowPopupTwo(row) : rowPopupOne(row);
  return decodeHtmlEntities(String(information || primary || fallback || "").trim());
}

function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/** Reverse countdown display (MM:SS). */
function formatCountdownMmSs(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function toBool(v) {
  if (v === true || v === 1) return true;
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

function toPositiveInt(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.floor(n);
  return i > 0 ? i : fallback;
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

/** Stages table stores `question_timer` as MM:SS (e.g. 00:20) or seconds as a number. */
function parseQuestionTimerToSeconds(value) {
  if (value == null) return 0;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }
  const s = String(value).trim();
  if (!s || s === "00:00") return 0;
  const mmss = /^(\d{1,3}):(\d{2})$/.exec(s);
  if (mmss) {
    const minutes = parseInt(mmss[1], 10);
    const sec = parseInt(mmss[2], 10);
    if (!Number.isFinite(minutes) || !Number.isFinite(sec)) return 0;
    return Math.max(0, minutes * 60 + sec);
  }
  const n = Number(s);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  return 0;
}

function stageQuestionPoint(value, fallback = 10) {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.trunc(n));
}

function shuffleQuestions(items) {
  const arr = Array.isArray(items) ? [...items] : [];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function FolderOutlineSvg({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="
          M12,0
          H148
          L168,36
          H288 Q300,36 300,48
          V228 Q300,240 288,240
          H12 Q0,240 0,228
          V12 Q0,0 12,0 Z
        "
        fill="none"
        stroke="rgba(255,255,255,0.95)"
        strokeWidth="2.5"
      />
    </svg>
  );
}

function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);

  const [loading, setLoading] = useState(true);
  const [deck, setDeck] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [points, setPoints] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [phase, setPhase] = useState("question");
  const [reveal, setReveal] = useState(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [hoverSide, setHoverSide] = useState(null);
  const [pickedSide, setPickedSide] = useState(null);
  const [selectedSideForReveal, setSelectedSideForReveal] = useState(null);
  const [startedAtMs] = useState(() => Date.now());
  const [questionTimeLeft, setQuestionTimeLeft] = useState(0);
  /** Re-render once per second when timer is off so session elapsed updates (forward clock). */
  const [, setForwardClockTick] = useState(0);
  /** From `stages` via fetchGameQuestions — per-question timer length and points. */
  const [stageRules, setStageRules] = useState(null);
  const questionTimeoutFiredRef = useRef(false);
  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);
  const replayGuardDoneRef = useRef(false);
  const initPromiseRef = useRef(null);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUserData = useMemo(() => readStoredUserData(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );
  const lobbyName = useMemo(
    () => resolveLobbyName(location.state),
    [location.state]
  );
  const effectiveLobbyName = useMemo(() => {
    const fromRoute = String(lobbyName || "").trim();
    if (fromRoute) return fromRoute;
    const fromUser = String(storedUserData?.lobby || "").trim();
    return fromUser;
  }, [lobbyName, storedUserData]);
  const lockedGamePath = useMemo(() => {
    const p = String(location.pathname || "/game");
    const s = String(location.search || "");
    const h = String(location.hash || "");
    return `${p}${s}${h}`;
  }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    const storedRid = String(
      storedUserData?.reportId || storedUserData?.reportid || ""
    ).trim();
    if (storedRid) {
      reportIdRef.current = storedRid;
    }
  }, [storedUserData]);
  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const sidLc = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oidLc = String(storedUserData?.organizationId ?? "").toLowerCase();
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sidLc === "demobypass" && oidLc === "demobypass");

  const persistIdsToStoredUserData = useCallback((nextReportId, nextStageId) => {
    try {
      const raw =
        localStorage.getItem("userData") ||
        sessionStorage.getItem("userData") ||
        "{}";
      const parsed = JSON.parse(raw) || {};
      const merged = {
        ...parsed,
        ...(nextReportId ? { reportId: String(nextReportId) } : {}),
        ...(nextStageId ? { stageId: String(nextStageId) } : {}),
      };
      localStorage.setItem("userData", JSON.stringify(merged));
      sessionStorage.setItem("userData", JSON.stringify(merged));
    } catch {
      // ignore storage failures
    }
  }, []);

  const ensureStageAndReportReady = useCallback(async () => {
    if (isDemoBypass || !adminToken || !backendBase) return;
    if (initPromiseRef.current) {
      await initPromiseRef.current;
      return;
    }

    const uid =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    const pinRaw = String(
      new URLSearchParams(window.location.search).get("pin") || ""
    ).trim();
    const pinNum = Number(pinRaw);
    const loginPinBody =
      pinRaw !== "" && Number.isFinite(pinNum)
        ? { login_pin: Math.trunc(pinNum) }
        : {};
    if (!uid || !sid || !oid) return;

    const run = (async () => {
      try {
        let currentStageId = stageRowIdRef.current;
        let currentReportId = reportIdRef.current;

        // 1) Try to read current stage/report from backend first.
        try {
          const fr = await fetch(`${backendBase}/fetchReport`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              lobby: effectiveLobbyName || "",
            }),
          });
          const frData = await fr.json().catch(() => ({}));
          const stageIdFromFetch = frData?.report?.id;
          if (stageIdFromFetch != null && String(stageIdFromFetch).trim() !== "") {
            currentStageId = String(stageIdFromFetch).trim();
            stageRowIdRef.current = currentStageId;
          }
          const reportIdFromFetch = frData?.report?.reportId;
          if (reportIdFromFetch != null && String(reportIdFromFetch).trim() !== "") {
            currentReportId = String(reportIdFromFetch).trim();
            reportIdRef.current = currentReportId;
          }
        } catch (fetchErr) {
          console.error("game init fetchReport:", fetchErr);
        }

        // 2) Ensure/add stage row and force backend lobby-template sync for timer/points.
        // addContent is idempotent and on existing rows it also aligns timer_status/question_timer/question_point.
        try {
          const addRes = await fetch(`${backendBase}/addContent`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              email: storedUserData.email || "",
              emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
              name: normalizePlayerName(storedUserData),
              reportId: currentReportId || storedUserData.reportId || storedUserData.reportid || "",
              gameId: storedUserData.gameId || "",
              user_type: reportRoleForApi,
              lobby: effectiveLobbyName || "",
              ...loginPinBody,
            }),
          });
          const addData = await addRes.json().catch(() => ({}));
          if (addRes.ok && addData?.success) {
            if (addData?.id != null && String(addData.id).trim() !== "") {
              currentStageId = String(addData.id).trim();
              stageRowIdRef.current = currentStageId;
            }
          }
        } catch (addErr) {
          console.error("game init addContent:", addErr);
        }

        // 3) Ensure report exists.
        if (!currentReportId) {
          const seedRes = await sendReport({
            sessionId: sid,
            organizationId: oid,
            role: reportRoleForApi,
            token: storedUserData.token || "",
            points: 0,
            time: "00:00",
            gameId: storedUserData.gameId,
            name: normalizePlayerName(storedUserData),
            userId: uid,
          });
          const seededRid = extractReportIdFromResponse(seedRes);
          if (seededRid) {
            currentReportId = String(seededRid).trim();
            reportIdRef.current = currentReportId;
          } else {
            // Guest add response shape can vary; re-read from backend after seed.
            try {
              const fr2 = await fetch(`${backendBase}/fetchReport`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  userId: uid,
                  sessionId: sid,
                  organizationId: oid,
                  lobby: effectiveLobbyName || "",
                }),
              });
              const fr2Data = await fr2.json().catch(() => ({}));
              const rid2 = fr2Data?.report?.reportId;
              if (rid2 != null && String(rid2).trim() !== "") {
                currentReportId = String(rid2).trim();
                reportIdRef.current = currentReportId;
              }
            } catch (reFetchErr) {
              console.error("game init fetchReport after seed:", reFetchErr);
            }
          }
        }

        // 4) Link both ids when available.
        if (currentStageId && currentReportId) {
          try {
            await fetch(`${backendBase}/welcomeStageLinkReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid,
                sessionId: sid,
                organizationId: oid,
                stageId: currentStageId,
                reportId: currentReportId,
              }),
            });
          } catch (linkErr) {
            console.error("game init welcomeStageLinkReport:", linkErr);
          }
        }

        persistIdsToStoredUserData(currentReportId, currentStageId);
      } catch (err) {
        console.error("game init stage/report:", err);
      } finally {
        initPromiseRef.current = null;
      }
    })();

    initPromiseRef.current = run;
    await run;
  }, [
    isDemoBypass,
    adminToken,
    backendBase,
    storedUserData,
    reportRoleForApi,
    effectiveLobbyName,
    persistIdsToStoredUserData,
    effectiveLobbyName,
  ]);

  useEffect(() => {
    dispatch(setBackButtonUrl(lockedGamePath || "/game"));
  }, [dispatch, lockedGamePath]);

  useEffect(() => {
    // Keep user on game page when browser back button is pressed repeatedly.
    window.history.pushState({ gameLock: true, step: 1 }, "", lockedGamePath);
    window.history.pushState({ gameLock: true, step: 2 }, "", lockedGamePath);
    const handlePopState = () => {
      window.history.pushState({ gameLock: true, step: 2 }, "", lockedGamePath);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [lockedGamePath]);

  /* ── Body background (unchanged) ── */
  useEffect(() => {
    const body = document.body;
    const prevBg = body.style.backgroundImage;
    const prevBgSize = body.style.backgroundSize;
    const prevBgPos = body.style.backgroundPosition;
    const prevBgRepeat = body.style.backgroundRepeat;
    const prevClasses = new Set(Array.from(body.classList));

    body.classList.add("game-active");
    body.classList.remove("common-bg", "common-bg1", "rules-bg");

    const applyGameBackground = () => {
      const mobilePortrait =
        window.innerWidth <= 768 &&
        window.matchMedia &&
        window.matchMedia("(orientation: portrait)").matches;
      const bgUrl = mobilePortrait ? game1MobileBg : game1DesktopBg;
      body.style.backgroundImage = bgUrl ? `url("${bgUrl}")` : "none";
      body.style.backgroundSize = "100% 100%";
      body.style.backgroundPosition = "center";
      body.style.backgroundRepeat = "no-repeat";
    };

    applyGameBackground();
    window.addEventListener("resize", applyGameBackground);
    window.addEventListener("orientationchange", applyGameBackground);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", applyGameBackground);
      vv.addEventListener("scroll", applyGameBackground);
    }

    return () => {
      window.removeEventListener("resize", applyGameBackground);
      window.removeEventListener("orientationchange", applyGameBackground);
      if (vv) {
        vv.removeEventListener("resize", applyGameBackground);
        vv.removeEventListener("scroll", applyGameBackground);
      }
      body.classList.remove("game-active");
      body.className = "";
      prevClasses.forEach((c) => body.classList.add(c));
      body.style.backgroundImage = prevBg;
      body.style.backgroundSize = prevBgSize;
      body.style.backgroundPosition = prevBgPos;
      body.style.backgroundRepeat = prevBgRepeat;
    };
  }, []);

  useEffect(() => {
    if (replayGuardDoneRef.current) return;
    if (!adminToken || !backendBase) return;
    const uid =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    if (!uid || !sid || !oid) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: sid,
            organizationId: oid,
            lobby: effectiveLobbyName || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data?.success || !data?.report) return;

        const rpt = data.report;
        const gameover = Number(rpt.gameover);
        if (!Number.isFinite(gameover) || gameover < 1) return;

        replayGuardDoneRef.current = true;
        const s1 = rpt.stage1 != null ? Number(rpt.stage1) : NaN;
        const s2 = rpt.stage2 != null ? Number(rpt.stage2) : NaN;
        const pts =
          Number(rpt.points) ||
          (Number.isFinite(s1) && Number.isFinite(s2) ? s1 + s2 : 0);
        navigate("/thankyou", {
          replace: true,
          state: {
            points: pts,
            time: rpt.time != null ? String(rpt.time) : "00:00",
            stageId: rpt.id != null ? String(rpt.id) : undefined,
            spellScore: Number.isFinite(s1)
              ? s1
              : Math.max(0, pts - (Number.isFinite(s2) ? s2 : 0)),
            flightScore: Number.isFinite(s2) ? s2 : 0,
          },
        });
      } catch (err) {
        console.error("game replay guard fetchReport:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, effectiveLobbyName, navigate, storedUserData]);

  /* ── Load questions ── */
  useEffect(() => {
    const loadQuestions = async () => {
      const sessionId = storedUserData.sessionId;
      const organizationId = storedUserData.organizationId;
      const userId =
        storedUserData.userId || storedUserData.userid || storedUserData.id;
      if (!sessionId || !organizationId || !adminToken || !backendBase) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // Link/QR: sync stage + lobby timer rules before fetchGameQuestions, or first load
        // reads default timer_off and shows forward elapsed until refresh.
        if (!isDemoBypass) {
          await ensureStageAndReportReady();
        }
        const res = await fetch(`${backendBase}/fetchGameQuestions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            sessionId,
            organizationId,
            lobby: effectiveLobbyName || "",
            userId: userId || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          throw new Error(data?.message || "Could not load questions.");
        }
        const list = Array.isArray(data.questions) ? data.questions : [];
        if (!list.length) {
          void Swal.fire({
            icon: "warning",
            title: "No Questions Found",
            text: "Please publish questions first.",
          }).then(() => navigate("/question", { replace: true }));
          return;
        }
        setDeck(shuffleQuestions(list));
        const st = data.stage;
        if (st && typeof st === "object") {
          setStageRules({
            timer_enabled: toBool(st.timer_status),
            timer_seconds: parseQuestionTimerToSeconds(st.question_timer),
            question_point: stageQuestionPoint(st.question_point, 10),
          });
        } else {
          setStageRules(null);
        }
      } catch (err) {
        console.error(err);
        void Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.message || "Failed to load questions.",
        });
      } finally {
        setLoading(false);
      }
    };
    loadQuestions();
  }, [
    adminToken,
    backendBase,
    effectiveLobbyName,
    ensureStageAndReportReady,
    isDemoBypass,
    navigate,
    storedUserData.organizationId,
    storedUserData.sessionId,
    storedUserData.userId,
    storedUserData.userid,
    storedUserData.id,
  ]);

  const row = deck[qIndex] || null;
  const total = deck.length;
  const rowPointValue = useMemo(() => {
    if (stageRules) return stageRules.question_point;
    return toPositiveInt(row?.question_point, 10);
  }, [row, stageRules]);
  const isQuestionTimerEnabled = useMemo(() => {
    if (!row) return false;
    if (stageRules) return stageRules.timer_enabled;
    return toBool(row.timer_status);
  }, [row, stageRules]);
  /** Parsed `question_timer` (MM:SS or seconds) — used only when `timer_status` is true for countdown. */
  const rowTimerSeconds = useMemo(() => {
    if (!row) return 0;
    if (stageRules) return stageRules.timer_seconds;
    return parseQuestionTimerToSeconds(row?.question_timer);
  }, [row, stageRules]);
  const totalPossiblePoints = useMemo(() => {
    if (!deck.length) return 0;
    const perQ = stageRules
      ? stageRules.question_point
      : toPositiveInt(deck[0]?.question_point, 10);
    return deck.length * perQ;
  }, [deck, stageRules]);

  useEffect(() => {
    questionTimeoutFiredRef.current = false;
  }, [qIndex, phase]);

  useEffect(() => {
    if (phase !== "question") return;
    if (!isQuestionTimerEnabled || rowTimerSeconds <= 0) {
      setQuestionTimeLeft(0);
      return;
    }
    setQuestionTimeLeft(rowTimerSeconds);
  }, [phase, qIndex, isQuestionTimerEnabled, rowTimerSeconds]);

  /** Forward session clock when per-question countdown is disabled (`timer_status` false). */
  useEffect(() => {
    if (isQuestionTimerEnabled) return;
    const id = window.setInterval(() => {
      setForwardClockTick((n) => n + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [isQuestionTimerEnabled]);

  const submitChoice = useCallback(
    async (selectedIdx) => {
      if (!row || phase !== "question" || isCommitting) return;
      const aid = parseInt(String(row.answer_id ?? "1"), 10);
      const correctIdx0 = aid === 2 ? 1 : 0;
      const timedOut = selectedIdx == null;
      const isCorrect = !timedOut && selectedIdx === correctIdx0;

      const opt1 = String(row.option_one || "Myth").trim();
      const opt2 = String(row.option_two || "Fact").trim();
      const correctLabel = correctIdx0 === 0 ? opt1 : opt2;
      const nextPoints = points + (isCorrect ? rowPointValue : 0);
      const isLast = qIndex + 1 >= total;
      const headline = timedOut
        ? `Time's up — it's a ${correctLabel}.`
        : isCorrect
        ? `Correct — it's a ${correctLabel}!`
        : `Not quite — it's a ${correctLabel}.`;
      const explainHtml = explainHtmlForReveal(row, correctIdx0);
      const picked = selectedIdx === 0 ? "left" : selectedIdx === 1 ? "right" : null;

      setIsCommitting(true);
      try {
        // Show the picked side fill for a moment, then transition to reveal
        setPickedSide(picked);
        setHoverSide(null);

        setTimeout(() => {
          setPoints(nextPoints);
          if (isCorrect) setCorrectCount((v) => v + 1);
          setSelectedSideForReveal(picked);
          setReveal({
            isCorrect,
            headline,
            explainHtml,
            questionHtml: String(row.question_name || ""),
            correctLabel,
            isLast,
          });
          setPhase("reveal");
          setPickedSide(null);
          setIsCommitting(false);

          if (
            isDemoBypass &&
            qIndex === 0 &&
            !demoRedirectTriggeredRef.current
          ) {
            demoRedirectTriggeredRef.current = true;
            setTimeout(() => {
              void Swal.fire({
                icon: "info",
                text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
                confirmButtonText: "OK",
                allowOutsideClick: false,
                allowEscapeKey: false,
              }).then(() => {
                window.location.assign("https://extramileplay.com/plans");
              });
            }, 400);
          }
        }, 380);
      } catch {
        setIsCommitting(false);
      }
    },
    [row, phase, isCommitting, points, qIndex, total, rowPointValue, isDemoBypass]
  );

  const completeGameAndExit = useCallback(async () => {
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const elapsedMs = Math.max(0, Date.now() - startedAtMs);
    const elapsedText = formatElapsed(elapsedMs);
    const wrongCount = Math.max(0, total - correctCount);

    if (backendBase && adminToken && userId && sessionId && organizationId) {
      try {
        setIsFinishing(true);
        await ensureStageAndReportReady();
        const res = await fetch(`${backendBase}/completeGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            lobby: effectiveLobbyName || "",
            points,
            total_score: points,
            total_questions: total,
            correct_count: correctCount,
            wrongcount: wrongCount,
            elapsed_ms: elapsedMs,
            time: elapsedText,
            currentQues: String(total),
            stage1: points,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          throw new Error(data?.message || "Could not save game result.");
        }
        if (data?.id != null && String(data.id).trim() !== "") {
          stageRowIdRef.current = String(data.id).trim();
        }
      } catch (err) {
        console.error(err);
        await Swal.fire({
          icon: "warning",
          title: "Result save failed",
          text:
            err?.message ||
            "Could not save result right now. You can continue.",
        });
      } finally {
        setIsFinishing(false);
      }
    }

    if (!isDemoBypass && sessionId && organizationId && userId) {
      try {
        if (!reportIdRef.current && backendBase && adminToken) {
          try {
            const fr = await fetch(`${backendBase}/fetchReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId,
                sessionId,
                organizationId,
                lobby: effectiveLobbyName || "",
              }),
            });
            const frData = await fr.json().catch(() => ({}));
            const rid = frData?.report?.reportId;
            if (rid != null && String(rid).trim() !== "") {
              reportIdRef.current = String(rid).trim();
            }
          } catch (fetchRidErr) {
            console.error("game fetch reportId before final send:", fetchRidErr);
          }
        }

        const reportRes = await sendReport({
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId,
          organizationId,
          role: reportRoleForApi,
          token: storedUserData.token || "",
          points,
          time: elapsedText,
          gameId: storedUserData.gameId,
          name: normalizePlayerName(storedUserData),
          userId,
        });
        const updatedRid = extractReportIdFromResponse(reportRes);
        if (updatedRid) {
          reportIdRef.current = String(updatedRid);
          persistIdsToStoredUserData(reportIdRef.current, stageRowIdRef.current);
        }
        if (
          updatedRid &&
          stageRowIdRef.current &&
          backendBase &&
          adminToken
        ) {
          try {
            await fetch(`${backendBase}/welcomeStageLinkReport`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId,
                sessionId,
                organizationId,
                stageId: stageRowIdRef.current,
                reportId: String(updatedRid),
              }),
            });
          } catch (linkErr) {
            console.error("game welcomeStageLinkReport:", linkErr);
          }
        }
      } catch (err) {
        console.error("game sendReport:", err);
      }
    }

    navigate("/thankyou", {
      replace: true,
      state: {
        points,
        maxScore: totalPossiblePoints,
        spellScore: points,
        stage1: points,
        wrongcount: wrongCount,
        time: elapsedText,
      },
    });
  }, [
    adminToken,
    backendBase,
    correctCount,
    effectiveLobbyName,
    navigate,
    points,
    startedAtMs,
    isDemoBypass,
    reportRoleForApi,
    storedUserData.id,
    storedUserData.gameId,
    storedUserData.organizationId,
    storedUserData.sessionId,
    storedUserData.token,
    storedUserData.userId,
    storedUserData.userid,
    total,
    totalPossiblePoints,
  ]);

  const handleQuestionTimeout = useCallback(() => {
    if (!row || phase !== "question") return;
    if (isCommitting || isFinishing) return;
    const isLast = qIndex + 1 >= total;
    setIsCommitting(true);
    setHoverSide(null);
    setPickedSide(null);
    if (isLast) {
      void completeGameAndExit().finally(() => {
        setIsCommitting(false);
      });
    } else {
      setQIndex((i) => i + 1);
      setPhase("question");
      setReveal(null);
      setIsCommitting(false);
    }
  }, [
    row,
    phase,
    isCommitting,
    isFinishing,
    qIndex,
    total,
    completeGameAndExit,
  ]);

  useEffect(() => {
    if (phase !== "question") return;
    if (!isQuestionTimerEnabled || rowTimerSeconds <= 0) return;
    if (isCommitting) return;
    if (questionTimeLeft <= 0) return;
    const timerId = window.setTimeout(() => {
      setQuestionTimeLeft((prev) => {
        if (prev <= 1) {
          if (!questionTimeoutFiredRef.current) {
            questionTimeoutFiredRef.current = true;
            void handleQuestionTimeout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [
    phase,
    isQuestionTimerEnabled,
    rowTimerSeconds,
    questionTimeLeft,
    isCommitting,
    handleQuestionTimeout,
  ]);

  const onRevealNext = useCallback(async () => {
    if (!reveal) return;
    if (reveal.isLast) {
      await completeGameAndExit();
      return;
    }
    setQIndex((i) => i + 1);
    setPhase("question");
    setReveal(null);
    setHoverSide(null);
    setPickedSide(null);
    setSelectedSideForReveal(null);
  }, [completeGameAndExit, reveal]);

  if (loading || !row) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  /* ── Compute split-bg class ── */
  const splitClass =
    phase === "reveal" && selectedSideForReveal
      ? `mf-split-bg mf-split-bg--picked-${selectedSideForReveal}`
      : pickedSide
    ? `mf-split-bg mf-split-bg--picked-${pickedSide}`
    : hoverSide
    ? `mf-split-bg mf-split-bg--hover-${hoverSide}`
    : "mf-split-bg";

  const opt1Label = String(row.option_one || "Myth").trim();
  const opt2Label = String(row.option_two || "Fact").trim();

  return (
    <div className="mf-root">
      {/* ── Split colour background ── */}
      <div className={splitClass} aria-hidden="true">
        <div className="mf-split-left" />
        <div className="mf-split-right" />
      </div>

      {/* ── Custom wave pattern overlay ── */}
      <div className="mf-wave-pattern" aria-hidden="true" />

      {/* ── Surface content ── */}
      <div className="mf-surface">
        {/* Timer + Points */}
        <div className="mf-top-row">
          <div className="mf-status-chip mf-status-chip--timer">
            <img src={timerIcon} alt="Timer" className="mf-status-chip-icon" />
            <span>
              {isQuestionTimerEnabled
                ? formatCountdownMmSs(questionTimeLeft)
                : formatElapsed(Date.now() - startedAtMs)}
            </span>
          </div>
          <div className="mf-points">
            <img src={scoreIcon} alt="Score" className="mf-status-chip-icon" />
            <span>{points}</span>
          </div>
        </div>

        {/* Page title */}
        <h1 className="mf-game-title">FACT or MYTH?</h1>

        {/* ── QUESTION PHASE ── */}
        {phase === "question" && (
          <div className="mf-dfl-stage">
            <div className="mf-dfl-horizontal-layout">
              {/* Folder card */}
              <div className="mf-dfl-center-content">
                <div className="mf-folder-card mf-folder-card--outline">
                  <FolderOutlineSvg className="mf-folder-svg" />
                  <span className="mf-folder-tab-text">
                    {String(row.category_name || "MYTH / FACT").toUpperCase()}
                  </span>
                  <div className="mf-folder-body mf-folder-body--question">
                    <div
                      className="mf-folder-question mf-q-html"
                      dangerouslySetInnerHTML={{
                        __html: String(row.question_name || ""),
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mf-dfl-choices-row">
                {/* Left pill */}
                <button
                  type="button"
                  className="mf-dfl-side-pill mf-dfl-side-pill--left"
                  disabled={isCommitting}
                  onMouseEnter={() => setHoverSide("left")}
                  onMouseLeave={() => setHoverSide(null)}
                  onFocus={() => setHoverSide("left")}
                  onBlur={() => setHoverSide(null)}
                  onClick={() => void submitChoice(0)}
                >
                  {opt1Label}
                </button>

                {/* Right pill */}
                <button
                  type="button"
                  className="mf-dfl-side-pill mf-dfl-side-pill--right"
                  disabled={isCommitting}
                  onMouseEnter={() => setHoverSide("right")}
                  onMouseLeave={() => setHoverSide(null)}
                  onFocus={() => setHoverSide("right")}
                  onBlur={() => setHoverSide(null)}
                  onClick={() => void submitChoice(1)}
                >
                  {opt2Label}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── REVEAL PHASE ── */}
        {phase === "reveal" && reveal && (
          <div className="mf-dfl-reveal">
            <div className="mf-reveal-answer-label">
              {String(reveal.correctLabel || "").toUpperCase()}
            </div>

            <div className="mf-reveal-panels">
              <div className="mf-reveal-panel-left">
                <FolderOutlineSvg className="mf-reveal-folder-svg" />
                <span className="mf-reveal-folder-tab-text">
                  {String(row.category_name || "MYTH / FACT").toUpperCase()}
                </span>
                <div className="mf-reveal-panel-left-body">
                  <div
                    className="mf-reveal-panel-left-copy mf-q-html"
                    dangerouslySetInnerHTML={{ __html: reveal.questionHtml }}
                  />
                </div>
              </div>

              <div className="mf-reveal-panel-right">
                <div
                  className="mf-reveal-panel-right-copy mf-q-html"
                  dangerouslySetInnerHTML={{
                    __html: reveal.explainHtml || reveal.headline || "",
                  }}
                />
              </div>
            </div>

            {/* Next / Finish button */}
            <div className="mf-dfl-next-wrap mf-dfl-next-wrap--center">
              <button
                type="button"
                className="mf-dfl-next mf-dfl-next--bar"
                disabled={isFinishing}
                onClick={onRevealNext}
              >
                {reveal.isLast ? "Finish" : "Next"}
                <span className="mf-dfl-next-arrow" aria-hidden="true">
                  →
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Game;
